import { useCallback, useEffect, useState } from 'react';

/**
 * Check if the target is inside viewport or not
 *
 * @param {boolean} [once] - should this execute only once or everytime the target becomes visible
 *
 * @return {{
 *   visible: boolean,
 *   attachTarget: (node: HTMLElement | null) => void;
 *   attachContainer: (node: HTMLElement | null) => void;
 * }}
 */
export function useViewportVisibility(once = false) {
  const [triggered, setTriggered] = useState(false);
  const [target, setTarget] = useState(null);
  const [container, setContainer] = useState(null);
  const [visible, setVisible] = useState(false);

  const attachTarget = useCallback((node) => {
    setTarget(node);
  }, []);
  const attachContainer = useCallback((node) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    // Skip if target element is not available yet
    if (!target) {
      return;
    }

    // Skip if only one execution is required and it's already happened
    if (once && triggered) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const observedEntry = entries[0];

        if (!observedEntry) {
          return;
        }

        const isVisible = observedEntry.isIntersecting;

        if (once) {
          if (isVisible) {
            setTriggered(true);
            setVisible(true);
          }
        } else {
          setVisible(isVisible);
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: 1.0,
      },
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, [container, once, target, triggered]);

  return {
    visible,
    attachTarget,
    attachContainer,
  };
}
